/**
 * Drains the offline-session queue on bootAccepted.
 *
 * Domain-pure: receives a port (`OfflineUploadPort`) it can call to ship each
 * StartTransaction → N MeterValues → StopTransaction triplet. The port hides
 * the WebSocket layer (Session 5). The runner persists status changes via
 * the OfflineQueueRepository.
 *
 * txId remapping: the start-response gives us the CSMS-assigned transactionId.
 * That id is then carried into the meter-value frames and the stop frame of
 * the SAME session.
 */
import type { DomainEventEmitter } from './events';
import { OfflineSessionStatus } from './types';
import type { ConnectorId, StationId } from './types';
import type { OfflineSessionSnapshot } from './OfflineSession';

export interface OfflineUploadFrames {
  readonly start: {
    readonly connectorId: ConnectorId;
    readonly idTag: string;
    readonly meterStartWh: number;
    /** Historical wall-clock at which the offline session started. */
    readonly startedAtMs: number;
  };
  readonly meterValues: readonly {
    readonly atMs: number;
    readonly energyWh: number;
    readonly powerW: number;
    readonly voltage: number;
    readonly socPct?: number;
  }[];
  readonly stop: {
    readonly meterStopWh: number;
    readonly stoppedAtMs: number;
    readonly reason: string;
  };
}

export interface OfflineUploadPort {
  /**
   * Uploads a single offline session and returns the CSMS-assigned
   * transactionId (number) on success. Throws on transport failure so the
   * runner can mark the row failed and stop.
   */
  upload(stationId: StationId, frames: OfflineUploadFrames): Promise<number>;
}

export interface OfflineQueuePort {
  byStation(stationId: StationId): Promise<readonly OfflineSessionSnapshot[]>;
  upsert(stationId: StationId, snap: OfflineSessionSnapshot): Promise<void>;
  delete(stationId: StationId, queueId: string): Promise<void>;
}

export interface OfflineSessionRunnerOptions {
  readonly stopReason?: string;
}

export interface OfflineRunReport {
  readonly attempted: number;
  readonly uploaded: number;
  readonly failed: number;
}

/**
 * Drain the queue once. Caller is responsible for invoking on bootAccepted.
 * Sequential by design: an OCPP CSMS expects ordered uploads, and any
 * concurrency here would just reorder the wire frames.
 */
export async function runOfflineQueue(
  stationId: StationId,
  emitter: DomainEventEmitter,
  queue: OfflineQueuePort,
  port: OfflineUploadPort,
  opts: OfflineSessionRunnerOptions = {},
): Promise<OfflineRunReport> {
  const stopReason = opts.stopReason ?? 'Local';
  const all = await queue.byStation(stationId);
  const pending = all
    .filter(
      (s) => s.status === OfflineSessionStatus.Queued || s.status === OfflineSessionStatus.Failed,
    )
    .slice()
    .sort((a, b) => a.startedAt - b.startedAt);

  let uploaded = 0;
  let failed = 0;

  for (const snap of pending) {
    await queue.upsert(stationId, { ...snap, status: OfflineSessionStatus.Uploading });
    emitter.emit({
      kind: 'OfflineQueueChanged',
      stationId,
      at: snap.startedAt,
      queueLength: pending.length - uploaded - failed,
    });

    const frames: OfflineUploadFrames = {
      start: {
        connectorId: snap.connectorId,
        idTag: snap.idTag,
        meterStartWh: snap.meterStartWh,
        startedAtMs: snap.startedAt,
      },
      meterValues: buildMeterValueTimeline(snap),
      stop: {
        meterStopWh: snap.meterStartWh + snap.energyDeliveredWh,
        stoppedAtMs: snap.startedAt + snap.duration * 1000,
        reason: stopReason,
      },
    };

    try {
      await port.upload(stationId, frames);
      await queue.delete(stationId, snap.queueId);
      uploaded++;
    } catch {
      await queue.upsert(stationId, { ...snap, status: OfflineSessionStatus.Failed });
      failed++;
      break; // stop on first failure — CSMS is likely offline
    }
  }

  emitter.emit({
    kind: 'OfflineQueueChanged',
    stationId,
    at: Date.now(),
    queueLength: all.length - uploaded,
  });

  return { attempted: pending.length, uploaded, failed };
}

function buildMeterValueTimeline(snap: OfflineSessionSnapshot): OfflineUploadFrames['meterValues'] {
  const out: OfflineUploadFrames['meterValues'][number][] = [];
  const interval = Math.max(1, snap.meterValueIntervalSec);
  const steps = Math.max(1, Math.floor(snap.duration / interval));
  for (let i = 1; i <= steps; i++) {
    const atMs = snap.startedAt + i * interval * 1000;
    const fraction = i / steps;
    const energyWh = snap.meterStartWh + snap.energyDeliveredWh * fraction;
    const powerW = (snap.energyDeliveredWh / snap.duration) * 3600;
    const sample: OfflineUploadFrames['meterValues'][number] = {
      atMs,
      energyWh: Math.round(energyWh),
      powerW: Math.round(powerW),
      voltage: snap.voltage,
    };
    if (snap.startSoC !== undefined && snap.endSoC !== undefined) {
      const soc = snap.startSoC + (snap.endSoC - snap.startSoC) * fraction;
      out.push({ ...sample, socPct: +soc.toFixed(2) });
    } else {
      out.push(sample);
    }
  }
  return out;
}
